import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LanguageProvider } from "./contexts/LanguageContext";
import { Toaster } from "./components/ui/sonner";
import VisitorTracker from "./components/VisitorTracker";
import useVersionWatcher from "./hooks/useVersionWatcher";
import HomePage from "./pages/HomePage";
import FAQ from "./pages/about/FAQ";
import UltraAdminLogin from "./pages/UltraAdminLogin";
import UltraAdminDashboard from "./pages/UltraAdminDashboard";
import UltraAdminSettings from "./pages/UltraAdminSettings";
import UltraAdminSalesReports from "./pages/UltraAdminSalesReports";
import UltraAdminDelegateMap from "./pages/UltraAdminDelegateMap";
import UltraAdminSection from "./pages/UltraAdminSection";
import CustomerLogin from "./pages/CustomerLogin";
import SupplierLogin from "./pages/SupplierLogin";
import CustomerDashboard from "./pages/CustomerDashboard";
import SupplierDashboard from "./pages/SupplierDashboard";
import Registration from "./pages/Registration";
import BlueZoneRegistration from "./pages/BlueZoneRegistration";
import BlueZoneLayoutAdd from "./pages/BlueZoneLayoutAdd";
import BlueZoneLayouts from "./pages/BlueZoneLayouts";
import BlueZoneLayoutEdit from "./pages/BlueZoneLayoutEdit";
import GreenZoneRegistration from "./pages/GreenZoneRegistration";
import LoginHub from "./pages/LoginHub";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import InviteAccept from "./pages/InviteAccept";
import SupplierSetPassword from "./pages/SupplierSetPassword";
import ContractorSetPassword from "./pages/ContractorSetPassword";
import MuteahhitLanding from "./pages/MuteahhitLanding";
import MuteahhitOner from "./pages/MuteahhitOner";
import MuteahhitAdaylariPublic from "./pages/MuteahhitAdaylariPublic";
import UltraAdminContractorRecommenders from "./pages/UltraAdminContractorRecommenders";
import UltraAdminMuteahhitAdaylari from "./pages/UltraAdminMuteahhitAdaylari";
import SubUserProfile from "./pages/SubUserProfile";
import SupplierRegister from "./pages/SupplierRegister";
import TedarikciLanding from "./pages/TedarikciLanding";
import PlatformRules from "./pages/PlatformRules";
import SystemGuide from "./pages/SystemGuide";
import AdminMessagesPage from "./pages/AdminMessagesPage";
import HallsPage from "./pages/HallsPage";
import HallEditorPage from "./pages/HallEditorPage";
import SuperAdminsPage from "./pages/SuperAdminsPage";
import UltraAdminServiceCatalogPage from "./pages/UltraAdminServiceCatalogPage";
import UltraAdminServiceOrdersPage from "./pages/UltraAdminServiceOrdersPage";
import CustomerStandServicesPage from "./pages/CustomerStandServicesPage";
import SuperAdminSetupPage from "./pages/SuperAdminSetupPage";
import GridSettingsPage from "./pages/GridSettingsPage";
import MagicAccept from "./pages/MagicAccept";
import MagicLogin from "./pages/MagicLogin";
import ContractSignPage from "./pages/ContractSignPage";
import AuditLogsPage from "./pages/AuditLogsPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import ContractorRegister from "./pages/ContractorRegister";
import ContractorDashboard from "./pages/ContractorDashboard";
import UltraAdminProductsPage from "./pages/UltraAdminProductsPage";
import UltraAdminOrganizerProductsPage from "./pages/UltraAdminOrganizerProductsPage";
import UltraAdminSupplierProductsPage from "./pages/UltraAdminSupplierProductsPage";
import UltraAdminDataHealthPage from "./pages/UltraAdminDataHealthPage";
import UltraAdminMediaHealthPage from "./pages/UltraAdminMediaHealthPage";
import UltraAdminStandRequestsPage from "./pages/UltraAdminStandRequestsPage";
import UltraAdminOrganizerCategoriesPage from "./pages/UltraAdminOrganizerCategoriesPage";
import UltraAdminModularPackagesPage from "./pages/UltraAdminModularPackagesPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tiny helper component: forwards /muteahhit-oner/:token to
// /tedarikci/aday-oner/:token so already-sent invitation emails keep working.
function LegacyMuteahhitOnerRedirect() {
  const { token } = useParams();
  return <Navigate to={`/tedarikci/aday-oner/${token}`} replace />;
}

// Global 401 interceptor — sadece geçersiz/süresi dolmuş token (401) → logout
// 403 = yetki yok ama token geçerli → logout YAPMA

// === Request URL rewrite: stale baked-in REACT_APP_BACKEND_URL (from old deployment) ===
// If the bundled URL points to an outdated host (previous preview/deploy) but the user is
// actually on a different origin (e.g. a custom production domain), rewrite outgoing
// API calls to use the current window.origin so they reach the correct backend.
try {
  const bakedUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
  const currentOrigin = window.location.origin;
  const isPreview = currentOrigin.includes('preview.emergentagent')
                 || currentOrigin.includes('localhost');
  if (bakedUrl && bakedUrl !== currentOrigin && !isPreview) {
    axios.interceptors.request.use((config) => {
      if (config.url && typeof config.url === 'string' && config.url.startsWith(bakedUrl)) {
        config.url = currentOrigin + config.url.slice(bakedUrl.length);
      }
      return config;
    });
    // Also patch native fetch, in case any code path uses window.fetch with the baked URL
    const _origFetch = window.fetch?.bind(window);
    if (_origFetch) {
      window.fetch = (input, init) => {
        if (typeof input === 'string' && input.startsWith(bakedUrl)) {
          input = currentOrigin + input.slice(bakedUrl.length);
        } else if (input && typeof input === 'object' && typeof input.url === 'string' && input.url.startsWith(bakedUrl)) {
          input = new Request(currentOrigin + input.url.slice(bakedUrl.length), input);
        }
        return _origFetch(input, init);
      };
    }
  }
} catch (_e) { /* no-op */ }

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const path = window.location.pathname;
      const requestUrl = error.config?.url || '';

      // URL'e göre hangi session logout yapılacağını belirle
      const isSupplierPath = path.startsWith('/tedarikci-admin');
      const isContractorPath = path.startsWith('/yuklenci-admin');
      const isUltraPath    = path.startsWith('/ultra-admin');
      const isCustomerPath = path.startsWith('/musteri-admin');

      // Güvenlik: Ultra Admin path'indeyken supplier API'den gelen 401'leri görmezden gel
      // (Eski/geçersiz cop31_supplier_token ile yapılan çağrılar ultra admin'i logout etmemeli)
      const isSupplierApiCall = requestUrl.includes('/api/supplier/') || requestUrl.includes('/supplier/');
      if (isUltraPath && isSupplierApiCall) {
        return Promise.reject(error);
      }

      let redirectTab = null;

      if (isSupplierPath && localStorage.getItem('cop31_supplier_token')) {
        localStorage.removeItem('cop31_supplier_token');
        localStorage.removeItem('cop31_supplier_user');
        redirectTab = 'supplier';
      } else if (isContractorPath && localStorage.getItem('cop31_contractor_token')) {
        localStorage.removeItem('cop31_contractor_token');
        localStorage.removeItem('cop31_contractor_user');
        redirectTab = 'contractor';
      } else if (isUltraPath && localStorage.getItem('cop31_ultra_token')) {
        localStorage.removeItem('cop31_ultra_token');
        localStorage.removeItem('cop31_ultra_user');
        redirectTab = 'ultra';
      } else if ((isCustomerPath || (!isSupplierPath && !isUltraPath && !isContractorPath)) && localStorage.getItem('cop31_token')) {
        localStorage.removeItem('cop31_token');
        localStorage.removeItem('cop31_user');
        redirectTab = 'blue';
      }

      if (redirectTab && !path.includes('/giris')) {
        window.location.href = `/giris?tab=${redirectTab}`;
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  // Seviye 2 — Detect new deploys at runtime and show a non-blocking toast
  // so users can refresh on their own time. Pairs with the Seviye-1
  // ChunkLoadError auto-reload installed in `index.js`.
  useVersionWatcher({
    onUpdateAvailable: () => {
      // The user's language preference is held inside LanguageContext, but
      // App() lives ABOVE the provider so we read the stored value directly.
      const isTr = (localStorage.getItem('cop31_language') || 'tr') === 'tr';
      toast.message(
        isTr ? 'Yeni sürüm yayında' : 'New version available',
        {
          description: isTr
            ? 'Sayfayı yenilemek hata olmadan çalışmasını sağlar.'
            : 'Refresh the page to load the latest version.',
          duration: Infinity,           // sticky until dismissed / acted upon
          action: {
            label: isTr ? 'Yenile' : 'Refresh',
            onClick: () => window.location.reload(),
          },
          // Stable id so duplicate fires (e.g. on visibility change) replace
          // rather than stack the same toast.
          id: 'app-version-update',
        }
      );
    },
  });

  useEffect(() => {
    // Test aşaması: session_only temizleme devre dışı — kullanıcı süresiz giriş kalır
    // Eski session_only_initiated_* key'lerini temizle (önceki logout sorununa yol açıyordu)
    Object.keys(localStorage).filter(k => k.startsWith('session_only_initiated_')).forEach(k => localStorage.removeItem(k));

    // Globally prevent mouse wheel from changing number input values
    const preventWheelOnNumber = (e) => {
      if (e.target.type === 'number') {
        e.target.blur();
      }
    };
    document.addEventListener('wheel', preventWheelOnNumber, { passive: true });

    // ── Session Timeout Enforcement ──────────────────────────────────────────
    // Her 60 saniyede bir aktif oturumların süresini kontrol et
    let sessionConfig = null;
    const checkSessionTimeout = async () => {
      try {
        if (!sessionConfig) {
          const res = await axios.get(`${API}/auth/public/session-config`);
          sessionConfig = res.data;
        }
        const now = Date.now();
        const path = window.location.pathname;

        // Customer (Blue/Green Zone) session check
        const customerToken = localStorage.getItem('cop31_token');
        const customerLoginTime = parseInt(localStorage.getItem('cop31_login_time') || '0');
        if (customerToken && customerLoginTime) {
          const cfg = sessionConfig.blue_zone;
          if (cfg?.enabled && cfg.timeout_minutes > 0) {
            const elapsedMin = (now - customerLoginTime) / 60000;
            if (elapsedMin > cfg.timeout_minutes) {
              localStorage.removeItem('cop31_token');
              localStorage.removeItem('cop31_user');
              localStorage.removeItem('cop31_login_time');
              if (path.startsWith('/musteri-admin')) {
                window.location.href = '/giris?tab=blue&reason=timeout';
              }
            }
          }
        }

        // Supplier session check
        const supplierToken = localStorage.getItem('cop31_supplier_token');
        const supplierLoginTime = parseInt(localStorage.getItem('cop31_supplier_login_time') || '0');
        if (supplierToken && supplierLoginTime) {
          const cfg = sessionConfig.supplier;
          if (cfg?.enabled && cfg.timeout_minutes > 0) {
            const elapsedMin = (now - supplierLoginTime) / 60000;
            if (elapsedMin > cfg.timeout_minutes) {
              localStorage.removeItem('cop31_supplier_token');
              localStorage.removeItem('cop31_supplier_user');
              localStorage.removeItem('cop31_supplier_login_time');
              if (path.startsWith('/tedarikci-admin')) {
                window.location.href = '/giris?tab=supplier&reason=timeout';
              }
            }
          }
        }

        // Ultra Admin session check
        const ultraToken = localStorage.getItem('cop31_ultra_token');
        const ultraLoginTime = parseInt(localStorage.getItem('cop31_ultra_login_time') || '0');
        if (ultraToken && ultraLoginTime) {
          const cfg = sessionConfig.ultra_admin;
          if (cfg?.enabled && cfg.timeout_minutes > 0) {
            const elapsedMin = (now - ultraLoginTime) / 60000;
            if (elapsedMin > cfg.timeout_minutes) {
              localStorage.removeItem('cop31_ultra_token');
              localStorage.removeItem('cop31_ultra_user');
              localStorage.removeItem('cop31_ultra_login_time');
              if (path.startsWith('/ultra-admin')) {
                window.location.href = '/giris?tab=ultra&reason=timeout';
              }
            }
          }
        }
      } catch (_) {
        // Ağ hatası: sessizce geç
      }
    };

    const sessionInterval = setInterval(checkSessionTimeout, 60000); // her 60 sn
    checkSessionTimeout(); // sayfa yüklenince hemen çalıştır

    return () => {
      document.removeEventListener('wheel', preventWheelOnNumber);
      clearInterval(sessionInterval);
    };
  }, []);

  return (
    <LanguageProvider>
      <div className="App">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <VisitorTracker />
          <Routes>
            <Route path="/" element={<Navigate to="/ana-sayfa" replace />} />
            <Route path="/ana-sayfa" element={<HomePage />} />
            {/* TR URLs */}
            <Route path="/giris" element={<LoginHub />} />
            <Route path="/green-zone-kayit" element={<GreenZoneRegistration />} />
            <Route path="/tedarikci-kayit" element={<SupplierRegister />} />
            <Route path="/tedarikci" element={<TedarikciLanding />} />
            {/* Phase 86AZ.60 — Contractor landing + Tedarikçi Aday Öner module
                (renamed Feb 2026: müteahhit aday öner → tedarikçi aday öner) */}
            <Route path="/muteahhit" element={<MuteahhitLanding />} />
            {/* New (tedarikçi) routes for the recommender module */}
            <Route path="/tedarikci/adaylar" element={<MuteahhitAdaylariPublic />} />
            <Route path="/tedarikci/aday-oner/:token" element={<MuteahhitOner />} />
            {/* Legacy redirects — keep old URLs from previously-sent invitation
                emails working forever. */}
            <Route path="/muteahhit/adaylar" element={<Navigate to="/tedarikci/adaylar" replace />} />
            <Route path="/muteahhit-oner/:token" element={<LegacyMuteahhitOnerRedirect />} />
            <Route path="/yuklenci-kayit" element={<ContractorRegister />} />
            <Route path="/kayit" element={<Registration />} />
            <Route path="/kayit/blue-zone" element={<BlueZoneRegistration />} />
            <Route path="/kayit/green-zone" element={<GreenZoneRegistration />} />
            {/* EN URL aliases */}
            <Route path="/login" element={<LoginHub />} />
            {/* Phase 86AY.3 — Standalone reset-password landing for invitation
                emails. Email & code are pre-filled from URL params when
                possible. */}
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/sifre-belirle" element={<ResetPasswordPage />} />
            <Route path="/registration" element={<Registration />} />
            <Route path="/registration/blue-zone" element={<BlueZoneRegistration />} />
            <Route path="/registration/green-zone" element={<GreenZoneRegistration />} />
            <Route path="/supplier-register" element={<SupplierRegister />} />
            <Route path="/contractor-register" element={<ContractorRegister />} />
            <Route path="/about/faq" element={<FAQ />} />
            {/* SummitFlow rebrand — legacy climate about-pages removed; redirect to home */}
            <Route path="/about/*" element={<Navigate to="/ana-sayfa" replace />} />
            <Route path="/ultra-admin" element={<UltraAdminLogin />} />
            {/* Ultra Admin Dashboard — tab bazlı URL */}
            <Route path="/ultra-admin/dashboard" element={<UltraAdminDashboard />} />
            <Route path="/ultra-admin/dashboard/:tab" element={<UltraAdminDashboard />} />
            <Route path="/ultra-admin/blue-zone" element={<BlueZoneLayouts />} />
            <Route path="/ultra-admin/blue-zone/new" element={<BlueZoneLayoutAdd />} />
            <Route path="/ultra-admin/blue-zone/edit/:id" element={<BlueZoneLayoutEdit />} />
            {/* Ultra Admin Settings — section + subsection bazlı URL */}
            <Route path="/ultra-admin/settings" element={<UltraAdminSettings />} />
            <Route path="/ultra-admin/settings/:section" element={<UltraAdminSettings />} />
            <Route path="/ultra-admin/settings/:section/:subsection" element={<UltraAdminSettings />} />
            <Route path="/ultra-admin/platform-rules" element={<PlatformRules />} />
            <Route path="/ultra-admin/system-guide" element={<SystemGuide />} />
            {/* Phase 86AZ.60 — Tedarikçi (recommender) admin section */}
            <Route path="/ultra-admin/tedarikci/onericiler" element={<UltraAdminContractorRecommenders />} />
            <Route path="/ultra-admin/tedarikci/adaylar" element={<UltraAdminMuteahhitAdaylari />} />
            {/* Legacy redirects — keep old admin bookmarks working */}
            <Route path="/ultra-admin/muteahhit/onericiler" element={<Navigate to="/ultra-admin/tedarikci/onericiler" replace />} />
            <Route path="/ultra-admin/muteahhit/adaylar" element={<Navigate to="/ultra-admin/tedarikci/adaylar" replace />} />
            <Route path="/ultra-admin/sales" element={<UltraAdminSalesReports />} />
            <Route path="/ultra-admin/delegate-map" element={<UltraAdminDelegateMap />} />
            <Route path="/ultra-admin/messages" element={<AdminMessagesPage />} />
            <Route path="/ultra-admin/halls" element={<HallsPage />} />
            <Route path="/ultra-admin/halls/:hallId" element={<HallEditorPage />} />
            <Route path="/ultra-admin/super-admins" element={<SuperAdminsPage />} />
            <Route path="/ultra-admin/service-catalog" element={<UltraAdminServiceCatalogPage />} />
            <Route path="/ultra-admin/service-orders" element={<UltraAdminServiceOrdersPage />} />
            <Route path="/musteri-admin/:slug/stand-services" element={<CustomerStandServicesPage />} />
            <Route path="/super-admin-setup" element={<SuperAdminSetupPage />} />
            <Route path="/ultra-admin/platform-products" element={<UltraAdminProductsPage />} />
            <Route path="/ultra-admin/platform-products/:subsection" element={<UltraAdminProductsPage />} />
            <Route path="/ultra-admin/platform-products/:subsection/:action" element={<UltraAdminProductsPage />} />
            <Route path="/ultra-admin/organizer-products" element={<UltraAdminOrganizerProductsPage />} />
            <Route path="/ultra-admin/supplier-products" element={<UltraAdminSupplierProductsPage />} />
            <Route path="/ultra-admin/data-health" element={<UltraAdminDataHealthPage />} />
            <Route path="/ultra-admin/media-health" element={<UltraAdminMediaHealthPage />} />
            <Route path="/ultra-admin/stand-requests" element={<UltraAdminStandRequestsPage />} />
            <Route path="/ultra-admin/organizer-categories" element={<UltraAdminOrganizerCategoriesPage />} />
            <Route path="/ultra-admin/modular-packages" element={<UltraAdminModularPackagesPage />} />
            <Route path="/ultra-admin/grid-settings" element={<GridSettingsPage />} />
            <Route path="/ultra-admin/audit-logs" element={<AuditLogsPage />} />
            {/* Magic Link — email içinden stand kabul sayfası (public) */}
            <Route path="/magic-accept" element={<MagicAccept />} />
            {/* Magic Login — email içinden şifresiz panel erişimi (public) */}
            <Route path="/magic-login" element={<MagicLogin />} />
            {/* Dijital Sözleşme İmzalama — email içinden (public) */}
            <Route path="/sozlesme-imza" element={<ContractSignPage />} />
            <Route path="/ultra-admin/users" element={<UltraAdminSection />} />
            <Route path="/ultra-admin/users/by-role" element={<UltraAdminSection />} />
            <Route path="/ultra-admin/users/new" element={<UltraAdminSection />} />
            <Route path="/ultra-admin/tenants" element={<UltraAdminSection />} />
            <Route path="/ultra-admin/tenants/customers" element={<UltraAdminSection />} />
            <Route path="/ultra-admin/tenants/suppliers" element={<UltraAdminSection />} />
            <Route path="/ultra-admin/tenants/new" element={<UltraAdminSection />} />
            <Route path="/ultra-admin/products" element={<UltraAdminSection />} />
            <Route path="/ultra-admin/products/categories" element={<UltraAdminSection />} />
            <Route path="/ultra-admin/orders" element={<UltraAdminSection />} />
            <Route path="/ultra-admin/orders/pending" element={<UltraAdminSection />} />
            <Route path="/ultra-admin/orders/completed" element={<UltraAdminSection />} />
            <Route path="/ultra-admin/orders/cancelled" element={<UltraAdminSection />} />
            <Route path="/ultra-admin/user-categories" element={<UltraAdminSection />} />
            {/* Customer Dashboard — section bazlı URL */}
            <Route path="/musteri-giris" element={<Navigate to="/giris" replace />} />
            <Route path="/musteri-admin/:slug/dashboard" element={<CustomerDashboard />} />
            <Route path="/musteri-admin/:slug/modular-stands/:standId" element={<CustomerDashboard />} />
            <Route path="/musteri-admin/:slug/moduler-standlar/:standId" element={<CustomerDashboard />} />
            <Route path="/musteri-admin/:slug/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/musteri-admin/:slug/:section" element={<CustomerDashboard />} />
            {/* Supplier Dashboard — section bazlı URL */}
            <Route path="/tedarikci-giris" element={<Navigate to="/giris?tab=supplier&only=1" replace />} />
            <Route path="/davet/:token" element={<InviteAccept />} />
            {/* Phase 86AZ.58 — Supplier manual-invite setup landing */}
            <Route path="/tedarikci-sifre-belirle/:token" element={<SupplierSetPassword />} />
            {/* Phase 86AZ.59 — Contractor manual-invite setup landing */}
            <Route path="/yuklenci-sifre-belirle/:token" element={<ContractorSetPassword />} />
            <Route path="/sub-user-profil" element={<SubUserProfile />} />
            {/* Supplier Dashboard — section + subsection + action bazlı URL */}
            <Route path="/tedarikci-admin/:slug/dashboard" element={<SupplierDashboard />} />
            <Route path="/tedarikci-admin/:slug/dashboard/:section" element={<SupplierDashboard />} />
            <Route path="/tedarikci-admin/:slug/dashboard/:section/:subsection" element={<SupplierDashboard />} />
            <Route path="/tedarikci-admin/:slug/dashboard/:section/:subsection/:action" element={<SupplierDashboard />} />
            <Route path="/tedarikci-admin/dashboard" element={<SupplierDashboard />} />
            <Route path="/tedarikci-admin/dashboard/:section" element={<SupplierDashboard />} />
            <Route path="/tedarikci-admin/dashboard/:section/:subsection" element={<SupplierDashboard />} />
            <Route path="/tedarikci-admin/dashboard/:section/:subsection/:action" element={<SupplierDashboard />} />
            {/* Contractor routes */}
            <Route path="/yuklenci-admin/:slug/dashboard" element={<ContractorDashboard />} />
            <Route path="/yuklenci-admin/dashboard" element={<ContractorDashboard />} />
          </Routes>
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;